import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react'
import { useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../../actions/index'
import { getUserAvatarName } from '../../../services/user/index'
import { CUSTOM_EVENT_GA4_EVENT } from '../../../services/event-tracking/index'
import { URL_MANAGE_PROFILES, URL_AI_CHAT } from '../../../services/url/constants'
import Form, {
  Checkbox, CHECKBOX_STYLES, TextInput, EmailInput,
} from '../../Form/index'
import Button, { TYPES, SIZES } from '../../Button/index'
import Sherpa, { TYPE_LARGE } from '../../Sherpa/index'
import { MAX_PROFILES } from '../../../services/user-profiles/index'
import { PLAN_CATEGORY_AI_ONLY } from '../../../services/plans/index'
import { ICON_TYPES } from '../../Icon.v2/index'
import UserAvatar from '../../UserAvatar/index'
import { H2 } from '../../Heading/index'
import { selectProfilePicturesMap } from '../../../services/community/identity/profile/redux/selectors'
import useCommunityEnabled from '../../../services/community/shared/useCommunityEnabled'

const CLASS_NAME_SHERPA = ['wiw__sherpa']

// eslint-disable-next-line max-len
function renderProfiles (profiles, staticText, selectProfile, openModal, communityProfilePictures, communityEnabled) {
  //
  if (profiles.size < 1) {
    return <Sherpa className={CLASS_NAME_SHERPA} type={TYPE_LARGE} />
  }

  return (
    <ul className="wiw__users">
      {profiles.map((profile, index) => {
        const profileUid = String(profile.get('uid'))
        const avatarPath = communityEnabled
          ? communityProfilePictures.getIn([profileUid, 'card'], '')
          : profile.getIn(['profilePicture', 'hdtv_190x266'], '')
        // Name preference in community mode, most specific first:
        //
        //   1. the COMMUNITY display name, fetched per uid alongside the avatar
        //   2. `profile.displayName` — the ACCOUNT name. ⚠️ Despite the identical field name
        //      this is "firstName or username", not the Community display name, so it is a
        //      fallback and not the intended value
        //   3. the legacy avatar name
        //
        // Before the per-uid backfill only the signed-in profile reached (1), so every other
        // profile in the picker dropped to (2) and showed the old pre-Community presentation
        // next to the new one.
        const communityName = communityEnabled
          ? communityProfilePictures.getIn([profileUid, 'displayName'], '')
          : ''
        const profileName = communityName
          || (communityEnabled && profile.get('displayName'))
          || getUserAvatarName(profile)
        return (
          <li className="wiw__user" key={profile.get('uuid')}>
            <Button
              type={TYPES.LINK}
              onClick={selectProfile}
              data-index={index}
              className="wiw__user-link"
            >
              <span className="wiw__avatar" role="presentation">
                <UserAvatar
                  path={avatarPath}
                  name={profileName}
                />
              </span>
              <span className="wiw__name">{profileName}</span>
            </Button>
          </li>
        )
      })}
      {profiles.size < MAX_PROFILES ? (
        <li className="wiw__user" key="add-profile">
          <div className="wiw__add-profile">
            <Button
              data-label={staticText.getIn(['data', 'addProfile'])}
              icon={ICON_TYPES.PLUS}
              type={TYPES.ICON_PRIMARY}
              onClick={openModal}
              size={SIZES.LARGE}
            >
              <span className="wiw__name">
                {staticText.getIn(['data', 'addProfile'])}
              </span>
            </Button>
          </div>
        </li>
      ) : null}
    </ul>
  )
}

function WhoIsWatchingAddProfileActions ({ staticText, creating }) {
  const { formState: { isValid } } = useFormContext()
  const disabled = !isValid || creating

  return (
    <div className="wiw__add-action">
      <Button
        disabled={disabled}
        type={TYPES.PRIMARY}
        className=""
        submit
      >
        {staticText.getIn(['data', 'addProfile'])}
      </Button>
    </div>
  )
}

WhoIsWatchingAddProfileActions.propTypes = {
  staticText: PropTypes.object.isRequired,
  creating: PropTypes.bool.isRequired,
}

function WhoIsWatching ({
  setFeatureTrackingDataPersistent,
  communityProfilePictures,
  setEventDefault,
  dismissedChooser,
  setAppRenderable,
  createProfile,
  selectProfile,
  userProfiles,
  userAccount,
  pushHistory,
  staticText,
  showPrompt,
  auth,
  user,
}) {
  const { enabled: communityEnabled } = useCommunityEnabled()
  const profiles = userProfiles.get('data', List())

  const profileError = userProfiles.get('error')
  const renderPreference = profiles.size === 1
  const creating = userProfiles.get('creating', false)
  const created = userProfiles.get('created')
  const userBillingSubscriptions = userAccount.getIn(['details', 'data', 'billing', 'subscriptions']) || Map()
  const planCategory = userBillingSubscriptions.getIn(['plan', 'planCategory'])
  const isAiOnly = planCategory === PLAN_CATEGORY_AI_ONLY

  const [modalVisible, setModalVisible] = useState(false)
  const [addProfileFormKey, setAddProfileFormKey] = useState(0)

  const emailValidationErrors = useMemo(() => ({
    isEmail: staticText.getIn(['data', 'emailValidError']),
  }), [staticText])

  const onAddProfileSubmit = useCallback((data) => {
    createProfile({
      user_account_id: auth.get('userAccountId'),
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      language: user.getIn(['data', 'language']),
      auth,
    })
  }, [auth, createProfile, user])

  const resetForm = useCallback(() => {
    setAddProfileFormKey((key) => key + 1)
  }, [])

  const setPreference = useCallback((name, checked) => {
    if (checked) {
      // GA4 event
      setEventDefault(CUSTOM_EVENT_GA4_EVENT.mergeDeep({
        event_category: 'Profile Selector',
        event_action: 'Click',
        event_label: 'who-is-watching',
      }))
    }
    setFeatureTrackingDataPersistent({
      data: Map({ dismissedProfileChooser: checked }),
      auth,
    })
  }, [auth])

  const chooseProfile = useCallback((e) => {
    e.preventDefault()
    const { currentTarget } = e
    const index = currentTarget.getAttribute('data-index')

    if (isAiOnly) {
      pushHistory({ url: URL_AI_CHAT })
    }

    selectProfile(index)
  }, [isAiOnly])

  const manageProfiles = useCallback(() => {
    showPrompt(false)
    setAppRenderable(true)
  }, [])

  const openModal = useCallback(() => {
    setModalVisible(true)
  })

  const closeModal = useCallback(() => {
    setModalVisible(false)
  })

  useEffect(() => {
    if (!creating && modalVisible && !profileError) setModalVisible(false)
    if (creating === false && created !== false) resetForm()
    // Only react to create lifecycle (not modal open). Including modalVisible here
    // closes the add-profile form as soon as "+" is clicked.
  }, [creating])

  let className = 'wiw'

  if (modalVisible) {
    className += ' wiw--add-profile'
  }

  return (
    <div className={className}>
      <div className="wiw__logo">
        <span role="presentation" className="gaia-logo gaia-logo--white" />
      </div>
      <div className="wiw__animation-container">
        <div className="wiw__modal">
          <div className="wiw__modal-content">
            {modalVisible ? (
              <React.Fragment>
                <Button
                  className="wiw__close-modal"
                  icon={ICON_TYPES.CLOSE}
                  onClick={closeModal}
                  size={SIZES.LARGE}
                  data-label="close"
                  type={TYPES.ICON}
                />
                <H2>
                  {staticText.getIn(['data', 'addAProfile'])}
                </H2>
                <p className="wiw__profile-info">
                  {staticText.getIn(['data', 'allProfilesWill'])}
                  {profileError ? (
                    <span className="wiw__error">
                      {staticText.getIn(['data', 'genericError'])}
                    </span>
                  ) : null}
                </p>
                <div>
                  <Form
                    key={addProfileFormKey}
                    onValidSubmit={onAddProfileSubmit}
                    className="wiw__form"
                    defaultValues={{
                      firstName: '',
                      lastName: '',
                      email: '',
                    }}
                  >
                    <TextInput
                      label={staticText.getIn(['data', 'firstName'])}
                      autocomplete="off"
                      name="firstName"
                      required
                    />
                    <TextInput
                      label={staticText.getIn(['data', 'lastName'])}
                      autocomplete="off"
                      name="lastName"
                    />
                    <EmailInput
                      note={staticText.getIn(['data', 'forPersonalized'])}
                      label={staticText.getIn(['data', 'email'])}
                      validationErrors={emailValidationErrors}
                      validations="isEmail"
                      name="email"
                      required
                    />
                    <WhoIsWatchingAddProfileActions
                      staticText={staticText}
                      creating={creating}
                    />
                  </Form>
                </div>
                {creating ? (
                  <Sherpa type={TYPE_LARGE} className={CLASS_NAME_SHERPA} />
                ) : null}
              </React.Fragment>
            ) : null}
            {!modalVisible ? (
              <React.Fragment>
                <div className="wiw__title">
                  <H2>
                    {staticText.getIn(['data', 'whoIsWatching'])}
                  </H2>
                </div>
                {renderPreference ? (
                  <div className="wiw__dismiss-profile-chooser">
                    <Form
                      className="wiw__form-pref"
                      defaultValues={{
                        dismissedProfileChooser: dismissedChooser,
                      }}
                    >
                      <Checkbox
                        note={staticText.getIn(['data', 'checkboxSubtext'])}
                        label={staticText.getIn(['data', 'checkboxText'])}
                        htmlValue="dismiss profile chooser"
                        style={CHECKBOX_STYLES.SECONDARY}
                        name="dismissedProfileChooser"
                        className="wiw__checkbox"
                        onChange={setPreference}
                        value={dismissedChooser}
                      />
                    </Form>
                  </div>
                ) : null}
                {renderProfiles(
                  profiles,
                  staticText,
                  chooseProfile,
                  openModal,
                  communityProfilePictures,
                  communityEnabled,
                )}
                <Button
                  className="wiw__manage"
                  onClick={manageProfiles}
                  type={TYPES.SECONDARY}
                  url={URL_MANAGE_PROFILES}
                >{staticText.getIn(['data', 'manageProfiles'])}
                </Button>
              </React.Fragment>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

WhoIsWatching.propTypes = {
  userProfiles: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  createProfile: PropTypes.func.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
}

export default connectRedux(
  (state) => ({
    communityProfilePictures: selectProfilePicturesMap(state),
    dismissedChooser: state.featureTracking.getIn(['data', 'dismissedProfileChooser'], false),
    staticText: state.staticText.getIn(['data', 'whoIsWatchingPage'], Map()),
    userProfiles: state.userProfiles,
    userAccount: state.userAccount,
    auth: state.auth,
    user: state.user,
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setFeatureTrackingDataPersistent: actions.featureTracking.setFeatureTrackingDataPersistent,
      resetOnboarding: actions.onboarding.resetOnboarding,
      createProfile: actions.userProfiles.createProfile,
      changeAuthProfile: actions.auth.changeAuthProfile,
      selectProfile: actions.userProfiles.selectProfile,
      setAppRenderable: actions.app.setAppRenderable,
      showPrompt: actions.userProfiles.showPrompt,
      setEventDefault: actions.eventTracking.setEventDefault,
      pushHistory: actions.navigation.pushHistory,
    }
  },
)(WhoIsWatching)
